import * as Styles from "./styles";
import { useState, useEffect, useRef } from "react";
import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/slide.css";
import { withTooltip, IconHelp, THEMES} from "cdk-radial";
import { useTranslation } from "react-i18next";
import IconNotificationButton from "./IconNotificationButton";
import { HeaderMenu } from "./UserMenu";
import { useHistory } from "react-router-dom";
import { useSideSheetStore } from "../components/SideSheet/useSideSheetStore";
import NotificationSheetContainer from "../notification/NotificationSheetContainer";
import HistoryContainer from "../history/HistoryContainer";
import { useWebViewStore } from "../webview/useWebViewStore";
import { useNotificationStore } from "../notification/NotificationStore";
import { useGlobalStore } from "../core/GlobalStore";
import {useAnalytics} from '../third-party-integration/amplitude';
import {
    AMPLITUDE_EVENTS,
} from '../../constants';
import GlobalSearchRemote from "../../components/GlobalSearchRemote/GlobalSearchRemote";
import { ErrorBoundary } from "../core/ErrorBoundary";
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import {useGlobalSearchStore} from "../global-search/useGlobalSearchStore";
import { ClickCallbackResponse } from 'global-search/dist/src/components/Distributed/types';
import useUserProfileStore from "../core/UserProfileStore";
import launchExternal from '../utils/launchExternal';
import { getAccessToken, oktaAuth } from "../okta/authService";
import { useApplicationStore } from '../home/ApplicationStore';
import { useIPRestrictionStore } from "../dealer-admin/ip-access/IPRestrictionStore";
import { useDriveStore } from "../drive/DriveStore";
import { focusHeaderIcon } from "./focusHeaderIcon";
import { useAmplitudeHelpStore } from "../third-party-integration/amplitude/useAmplitudeHelpStore";
import Help from "../help/Help";

export const generateTooltipProps = (
  iconName: string,
  displayName: string
) => ({
  id: `header-tooltip-${iconName}`,
  text: displayName,
  theme: THEMES.LIGHT,
  styles: { zIndex: 1002, display: window.innerWidth < 501 && "none" },
});

function NavigationActionIcon(){
  return (
    <svg width="14" height="14" viewBox="0 0 15 15"  fill="#939393">
      <g clipPath="url(#clip0_138_16151)">
      <path d="M1.5 12.2998C1.5 12.9211 1.9721 13.4326 2.57715 13.4941L2.7002 13.5H12.2998C12.9625 13.5 13.5 12.9625 13.5 12.2998V2.7002C13.5 2.03745 12.9625 1.5 12.2998 1.5V0C13.791 0 15 1.20903 15 2.7002V12.2998C15 13.791 13.791 15 12.2998 15H2.7002C1.20903 15 0 13.791 0 12.2998V2.7002C0 1.20903 1.20903 0 2.7002 0V1.5L2.57715 1.50586C1.9721 1.56737 1.5 2.07892 1.5 2.7002V12.2998ZM12.2998 0V1.5H2.7002V0H12.2998Z"  fill="#939393"/>
      <path d="M5 1.5H6.5V13.5H5V1.5Z"  fill="#939393"/>
      </g>
      <defs>
      <clipPath id="clip0_138_16151">
      <rect width="15" height="15"  fill="#939393"/>
      </clipPath>
      </defs>
    </svg>
  )
}

export default function Header() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { openSideSheet } = useSideSheetStore();
  const { openHelp ,} = useAmplitudeHelpStore();

  const { resetActive, setCustomWebView, webviews, setActiveWebView } = useWebViewStore();
  const { setNavBarCollapsed , isNavBarCollapsed } = useGlobalStore() as any;
  const count = useNotificationStore((state: any) => state.count);
  const { trackEvent } = useAnalytics();
  const userProfile = useUserProfileStore();

  const storeId = userProfile?.currentStoreId || "";
  const userName = userProfile?.login || "";
  const [accessToken, setAccessToken] = useState('');
  const globalSearchFlag = useFeatureFlag('UNIFY_UI_GLOBAL_SEARCH_20');
  const isAmplitudeRCEnabled = useFeatureFlag('UNIFY_UI_AMPLITUDE_RC');
  const [query, setQuery] = useState('');

  const userApplications = useApplicationStore((state:any) => state.applications);
  const userIpRestriction = useIPRestrictionStore((state: any) => state.ipRestriction);
  const { dmsMapping, cNumbers } = useDriveStore();
  const dmsBoxMappings = (dmsMapping as any)?.dmsBoxMappings ?? [];
  const driveCNumbersWithAlias = cNumbers?.map((cNumber: string) => {
    const alias = dmsBoxMappings.find(
      (box: any) => box?.cnumber?.toLowerCase() === cNumber?.toLowerCase()
    )?.alias;
    return { cNumber, alias };
  }) ?? [];
  const [isAnimating, setIsAnimating] = useState(false);
  const previousWebviewsCountRef = useRef(webviews?.length || 0);

  const CDKGlobalLogo = withTooltip(
    Styles.Logo,
    generateTooltipProps("CDK Global Logo", t("cdklogo.toolTip"))
  );

  const HeaderHistoryIcon = withTooltip(
    Styles.AnimationButton,
    generateTooltipProps("History", t("history.toolTip"))
  );

  const HeaderHelpIcon = withTooltip(
    Styles.HeaderButton,
    generateTooltipProps("Help", t("help.toolTip"))
  );

  const HeaderExpandCollapse = ({ text, onClick ,icon}:any) => {
    const Component = withTooltip(Styles.ExpandCollapseButton,
      generateTooltipProps('ExpandCollapse', text)
    );
    return <Component text={text} onClick={onClick}
                backgroundAppearance={'dark'}
                icon={icon}
                dataTestId="header-icon-expand-collapse"/>;
  };

  const goHome = () => {

    const lastOpenedItem = webviews && webviews[0];
    if (!window.location.pathname.includes("/unify/wview") && !window.location.pathname.includes("/unify/aview") 
      && !window.location.pathname.includes("/unify/unify-admin")
      && !window.location.pathname.includes("/unify/dealer-admin")
      && !window.location.pathname.includes("/unify/global-search")
      && lastOpenedItem) {
        trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED,{'Item': 'Previous Application'});
        if (lastOpenedItem.type === 'application') {
          history.push(`/unify/aview/${lastOpenedItem.id}/N`);
        } else {
          history.push(`/unify/wview/${lastOpenedItem.appId}/${lastOpenedItem.id}/N`);
        }
        setActiveWebView(lastOpenedItem.id);
    }
      else {
        history.push("/unify/home");
        resetActive();
      };
  }

  // On touch devices handle the tap here and cancel the emulated mouse/click events,
  // otherwise the first tap is consumed by hover/tooltip or goHome fires twice
  const onLogoTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    goHome();
  }

  const genSideSheetHandler = () => () => {
    const title =
      count > 0
        ? `${t("notification.sheet.title")} (${count})`
        : t("notification.sheet.title");
    trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, {'Item': 'Notifications'});
    openSideSheet({
      title: title,
      dataTestId: 'notification-tab-header',
      body: <NotificationSheetContainer />,
      onClose: () => focusHeaderIcon('header-icon-notification'),
    });
  };

  const onHistoryClick = () => {
    trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, {'Item': 'Opened Apps and Workflows'});
    openSideSheet({
      title: t("history.name"),
      dataTestId: 'history-tab-header',
      body: <HistoryContainer />,
      onClose: () => focusHeaderIcon('header-icon-history'),
    });
  };

  const onHelpClick = () => {
    trackEvent(AMPLITUDE_EVENTS.HEADER_ITEM_SELECTED, {'Item': 'Help'});
    if(isAmplitudeRCEnabled){
      openHelp();
    }else{
      openSideSheet({
        title: t("help.name"),
        dataTestId: 'help-tab-header',
        body: <Help />,
        onClose: () => focusHeaderIcon('header-icon-help'),
      })
    }
  };

  //Global Search Start
  const searchQuery = useGlobalSearchStore((state: any) => state.searchQuery);
  const setSearchQuery = useGlobalSearchStore((state: any) => state.setSearchQuery);
  const setPreviousRoute = useGlobalSearchStore((state: any) => state.setPreviousRoute);

  useEffect(() => {
    setQuery(searchQuery || '');
  }, [searchQuery]);

   useEffect(() => {
    const fetchToken = async () => {
      const token = await getAccessToken();
      if (token) setAccessToken(token);
    };
    fetchToken();

    const handleRenewed = (key: string, newToken: any) => {
      if (key === 'accessToken' && newToken?.accessToken) {
        setAccessToken(newToken.accessToken);
      }
    };
    oktaAuth?.tokenManager?.on('renewed', handleRenewed);

    return () => {
      oktaAuth?.tokenManager?.off('renewed', handleRenewed);
    };
  }, []);

  const handleViewMore = (queryParam: string) => {
    const path = window.location.pathname ? window.location.pathname : '/unify/applications';
    if (window.location.pathname !== '/unify/pulse'
      && window.location.pathname !== '/unify/global-search'
      && !window.location.pathname.includes('/unify/dealer-admin')
      && !window.location.pathname.includes('/unify/unify-admin')
      && window.location.pathname !== '/unify/global-search-history'
      && window.location.pathname !== '/unify/cta') {
      setPreviousRoute(path);
    }
    resetActive();
    history.push("/unify/global-search");
    setSearchQuery(queryParam);
  }

  const handleSearchHistory = () => {
    const path = window.location.pathname;
    setPreviousRoute(path);
    resetActive();
    history.push("/unify/global-search-history");
  }

  const openGlobalSearchItem = (response: ClickCallbackResponse) => {
    try {
      if (response.url.includes("unify") && response.url.endsWith("/admin")) {
        history.push(`/unify/unify-admin`);
      }
      else {
        if (!launchExternal(response.url)) {
          history.push('/unify/cta')
          setCustomWebView({ id: response.id, url: response.url });
        }
      }
    } catch {
    }
  };

  const [userWorkflows, setUserWorkflows] = useState();

  useEffect(() => {
     const workflows = userApplications?.flatMap((app: any) =>
    (app.workflows ?? []).map(({ workflowId, ...rest }: any) => ({
      ...rest,
      id: workflowId,
      appId: app.id,
      appName: app.name
    }))
  );
  setUserWorkflows(workflows);
  }, [userApplications]);

  const shouldShowGS =
    globalSearchFlag === true &&
    Boolean(accessToken) &&
    Array.isArray(userApplications) &&
    userApplications.length > 0;

  const UNIFY_GS_APPLICATION = useFeatureFlag('UNIFY_GS_APPLICATION');
  const UNIFY_GS_WORKFLOW = useFeatureFlag('UNIFY_GS_WORKFLOW');
  const UNIFY_GS_DOCUMENT = useFeatureFlag('UNIFY_GS_DOCUMENT');
  const UNIFY_GS_CUSTOMER = useFeatureFlag('UNIFY_GS_CUSTOMER');
  const UNIFY_GS_VEHICLE = useFeatureFlag('UNIFY_GS_VEHICLE');
  const UNIFY_GS_VENDOR = useFeatureFlag('UNIFY_GS_VENDOR');
  const UNIFY_GS_DEAL = useFeatureFlag('UNIFY_GS_DEAL');
  const UNIFY_GS_MRDOC = useFeatureFlag('UNIFY_GS_MRDOC');
  const UNIFY_GS_EMPLOYEE = useFeatureFlag('UNIFY_GS_EMPLOYEE');
  const UNIFY_GS_MRCUSTOMER = useFeatureFlag('UNIFY_GS_MRCUSTOMER');
  const UNIFY_UI_GS_NLP = useFeatureFlag('UNIFY_UI_GS_NLP');

  const [harnessFlags, setHarnessFlags] = useState({});

    useEffect(() => {
    setHarnessFlags({
      UNIFY_GS_APPLICATION: UNIFY_GS_APPLICATION,
      UNIFY_GS_WORKFLOW: UNIFY_GS_WORKFLOW,
      UNIFY_GS_DOCUMENT: UNIFY_GS_DOCUMENT,
      UNIFY_GS_CUSTOMER: UNIFY_GS_CUSTOMER,
      UNIFY_GS_VEHICLE: UNIFY_GS_VEHICLE,
      UNIFY_GS_VENDOR: UNIFY_GS_VENDOR,
      UNIFY_GS_DEAL: UNIFY_GS_DEAL,
      UNIFY_GS_MRDOC: UNIFY_GS_MRDOC,
      UNIFY_GS_EMPLOYEE: UNIFY_GS_EMPLOYEE,
      UNIFY_GS_MRCUSTOMER: UNIFY_GS_MRCUSTOMER,
      UNIFY_UI_GS_NLP: UNIFY_UI_GS_NLP
    })
  }, [
    UNIFY_GS_EMPLOYEE,
    UNIFY_GS_MRDOC,
    UNIFY_GS_DEAL,
    UNIFY_GS_VENDOR,
    UNIFY_GS_VEHICLE,
    UNIFY_GS_CUSTOMER,
    UNIFY_GS_DOCUMENT,
    UNIFY_GS_WORKFLOW,
    UNIFY_GS_APPLICATION,
    UNIFY_GS_MRCUSTOMER,
    UNIFY_UI_GS_NLP,
    setHarnessFlags
  ])

  //Global Search End

  useEffect(() => {
    const currentWebviewsCount = webviews?.length || 0;
    if (currentWebviewsCount > previousWebviewsCountRef.current) {
      setIsAnimating(true);
    }
    previousWebviewsCountRef.current = currentWebviewsCount;
  }, [webviews]);

  // Channel Messaging - End

  const [showToolTip, setShowToolTip] = useState(false);
  const [expandCollapse, setExpandCollapse] = useState(false);
  
  useEffect(() => {
    if (window.location.pathname.includes("/unify/applications")
      || window.location.pathname.includes("/unify/workflows")
      || window.location.pathname.includes("/unify/myUnify")
      || window.location.pathname.includes("/unify/dashboards")
    ) {
      setShowToolTip(false);
      setExpandCollapse(false);
    }
    else {
      setShowToolTip(true);
      setExpandCollapse(true);
    }
    // eslint-disable-next-line 
  }, [window.location.pathname])

  return (
    <Styles.PageHeader>
      <div style={{display:'flex',flexDirection:'row',gap:'16px'}}>
        {showToolTip ?
          <CDKGlobalLogo
            height="32"
            viewBox="0 0 300 74"
            width="116"
            text={t("cdklogo.tooltip")}
            data-testid="global-header-logo"
            onClick={goHome}
            onTouchEnd={onLogoTouchEnd}
            data-track-label
            tabIndex={0}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter') {
                goHome();
              }
            }}
          /> :
          <div>
          <Styles.Logo
            height="32"
            viewBox="0 0 300 74"
            width="116"
            data-testid="global-header-logo"
            onClick={goHome}
            onTouchEnd={onLogoTouchEnd}
            data-track-label
            tabIndex={0}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter') {
                goHome();
              }
            }}
          />
          </div>
        }
      <div style={{width:'36px'}}>
        { expandCollapse && 
            <HeaderExpandCollapse
                onClick={() => setNavBarCollapsed(!isNavBarCollapsed)}
                backgroundAppearance={'dark'}
                icon={<NavigationActionIcon />}
                text={ isNavBarCollapsed ? t('header.expand') : t('header.collapse') }
                dataTestId="header-icon-expand-collapse"
            />
        } 
      </div>
      
      </div>
        {shouldShowGS ?
                    <ErrorBoundary>
                      <GlobalSearchRemote
                          variant="search"
                          style={{  }}
                          accessToken={accessToken}
                          onChange={(value: string) => setQuery(value)}
                          query={query}
                          storeId={storeId}
                          userName={userName}
                          viewMore={handleViewMore}
                          viewSearchHistory={handleSearchHistory}
                          onClickCallback={openGlobalSearchItem}
                          lng={i18n.language}
                          unifyUserApplications={userApplications}
                          unifyUserWorkflows={userWorkflows}
                          ipRestrictionDetails={userIpRestriction}
                          harnessFlags={harnessFlags}
                          cNumbers={driveCNumbersWithAlias}
                          />
                    </ErrorBoundary>: <div></div>}

      <Styles.PageHeaderIcons>
        
        <HeaderHistoryIcon
          onClick={() => { onHistoryClick(); }}
          backgroundAppearance={"dark"}
          icon={<Styles.ApplicationAnimatedIcon />}
          text={"1"}
          className={isAnimating ? "trigger-animate" : ""}
          dataTestId="header-icon-history"
          data-track-label
          onAnimationEnd={() => { setIsAnimating(false); }}
        />
        <HeaderHelpIcon
          onClick={() => onHelpClick()}
          backgroundAppearance={"dark"}
          icon={<IconHelp />}
          text={t("header.help")}
          dataTestId="header-icon-help"
          data-track-label="Help"
        />
        <IconNotificationButton onClick={genSideSheetHandler()} />
        <HeaderMenu />
      </Styles.PageHeaderIcons>
    </Styles.PageHeader>
  );
}
